import { AssetConfigurationsService } from './../asset-configurations.service';
import { ProductAssetsService } from './product-assets.service';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { Component, OnInit, ViewChild, Inject, ViewContainerRef, AfterViewInit } from '@angular/core';
import { DetailDataBoundEventArgs, DetailRowService, GridModel, RowDragEventArgs, RowDropEventArgs, SelectionSettingsModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AssetDTOStargate, AssetSyncStatus, AssetType, IAssetDTOStargate, ProductAssetDTOStargate } from 'src/app/base/models/models.swagger.generated';
import { ProductAssetsSingleEditComponent } from './product-assets-edit/product-assets-single-edit/product-assets-single-edit.component';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { ProductSyncStatus } from 'src/app/base/models/models';
import { CommonModule } from '@angular/common';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { ProductAssetsMultiUploadComponent } from './product-assets-edit/product-assets-multi-upload/product-assets-multi-upload.component';
import { SfRequestTypes } from 'src/app/base/models/syncfusion-data';
import { EnumFilterDataManager } from 'src/app/base/shared/syncfusion/enum-filter-data-manager';
import { EnumFilterService } from 'src/app/base/services/enum-filter.service';

interface IExtendedAssetDTOStargate extends IAssetDTOStargate {
  isDeletable: boolean;
  canBeSetAsKey: boolean;
}

export type ProductSyncStatusEnumFilterType = {
  syncState: ProductSyncStatus;
}
export enum HasAssets {
  false = 0,
  true = 1
}
export type AssetStatusEnumFilterType = {
  hasAssets: HasAssets;
}

@Component({
  selector: 'app-product-assets',
  templateUrl: './product-assets.component.html',
  styleUrls: ['./product-assets.component.scss'],
  providers: [DetailRowService],
  imports: [TitleBarComponent, GridModule, QueryBuilderModule, CommonModule, ProductAssetsSingleEditComponent, ProductAssetsMultiUploadComponent, TranslateModule]
})
export class ProductAssetsComponent extends GridBaseComponent<any> implements OnInit, AfterViewInit {
  @ViewChild(ProductAssetsSingleEditComponent)
  public productAssetsEditFormComponent: ProductAssetsSingleEditComponent;

  @ViewChild(ProductAssetsMultiUploadComponent)
  public productAssetsMultiUploadComponent: ProductAssetsMultiUploadComponent;

  //Syncfusion workaround for child grid templates : https://www.syncfusion.com/forums/161004/grid-child --------------
  @ViewChild("childtypetemplate", { static: true })
  public childtypetemplate: any;

  @ViewChild("childsyncstatustemplate", { static: true })
  public childsyncstatustemplate: any;

  @ViewChild("childkeytemplate", { static: true })
  public childkeytemplate: any;

  @ViewChild("childshopstemplate", { static: true })
  public childshopstemplate: any;

  @ViewChild("gridcommandstemplate", { static: true })
  public gridcommandstemplate: any;

  @ViewChild("previewTemplate", { static: true })
  public previewTemplate: any;
  //------------------------------------------------------------

  public childGrid: GridModel;
  public selectionOptions: SelectionSettingsModel = { checkboxMode: 'Default' };
  public selectedProducts: ProductAssetDTOStargate[] = [];
  // create filter settings per field since template infos are added here
  public hasAssetFilterSettings = GridConstantsFactory.createCheckBoxFilter();
  public syncStateFilterSettings = GridConstantsFactory.createCheckBoxFilter();
  public hasAssetsField = 'hasAssets';
  public syncStateField = 'syncState';

  private productSyncStatusEnumFilterValues: ProductSyncStatusEnumFilterType[] = [];
  private assetStatusEnumFilterValues: AssetStatusEnumFilterType[] = [];

  private expandedRowId: string = null;
  //used to open correct product again afterchanging keys or delete in the child grid
  private openExpandedRowOnRefresh: boolean = false;
  private rowsToExpandAfterRefresh: number[];

  constructor(
    translateService: TranslateService,
    private productAssetsService: ProductAssetsService,
    private spinnerService: SpinnerService,
    private assetConfigurationsService: AssetConfigurationsService,
    private enumFilterService: EnumFilterService,
    //Syncfusion workaround for child grid templates : https://www.syncfusion.com/forums/161004/grid-child
    @Inject(ViewContainerRef) private viewContainerRef?: ViewContainerRef,
  ) {
    super(translateService, productAssetsService);
  }

  public ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.productAssetsService, this.spinnerService),
    });

    //Assure that translateService is initialized before
    this.translateService.get('i18n.domain.assets.product-assets.subgrid.preview').subscribe((value) => {
      this.childGrid = {
        load: function () {
          this.registeredTemplate = {}; //set registertemplate value as empty in load event
        },
        allowRowDragAndDrop: true,
        rowDrop: this.onRowDrop.bind(this),
        rowDrag: this.onDrag.bind(this),
        columns: [
          { field: 'fileLink', headerText: value, clipMode: "EllipsisWithTooltip", template: this.previewTemplate },
          { field: 'publicName', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.public-name'), clipMode: "EllipsisWithTooltip" },
          { field: 'originalFileName', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.original-file-name'), clipMode: "EllipsisWithTooltip" },
          { field: 'languageCodeIso3', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.language') },
          { field: 'type', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.type'), template: this.childtypetemplate },
          { field: 'shopIds', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.shops'), template: this.childshopstemplate, clipMode: "EllipsisWithTooltip" },
          { field: 'syncStatus', headerText: this.translateService.instant('i18n.domain.assets.product-assets.subgrid.shop-state'), template: this.childsyncstatustemplate },
          { field: '', headerText: "", template: this.gridcommandstemplate, width: '120px' }
        ]
      };
    });

    const productSyncStatusenumType = ProductSyncStatus;
    this.productSyncStatusEnumFilterValues = this.enumFilterService.createEnumFilterArray(productSyncStatusenumType, this.syncStateField);
    const assetStatusenumType = HasAssets;
    this.assetStatusEnumFilterValues = this.enumFilterService.createEnumFilterArray(assetStatusenumType, this.hasAssetsField);
  }

  //Syncfusion workaround for child grid templates : https://www.syncfusion.com/forums/161004/grid-child
  public ngAfterViewInit() {
    this.childtypetemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
    this.childsyncstatustemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
    this.childkeytemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
    this.childshopstemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
    this.gridcommandstemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
    this.previewTemplate.elementRef.nativeElement._viewContainerRef = this.viewContainerRef;
  }

  public override actionBegin(args: any): void {
    if (args.requestType === SfRequestTypes.FilterBeforeOpen) {
      if (args.columnName === this.syncStateField) {
        args.filterModel.options.dataSource = new EnumFilterDataManager<ProductSyncStatusEnumFilterType>(this.productSyncStatusEnumFilterValues);
      }
      if (args.columnName === this.hasAssetsField) {
        args.filterModel.options.dataSource = new EnumFilterDataManager<AssetStatusEnumFilterType>(this.assetStatusEnumFilterValues);
      }
    }
    if (args.requestType === SfRequestTypes.FilterChoiceRequest && (args.filterModel.options.field === this.syncStateField || args.filterModel.options.field === this.hasAssetsField)) {
      args.query.distincts = []; // will set custom trigger flag and avoid filtering the list for distinct values of the enum filter field
      args.filterModel.options.dataManager = null;
    }

    super.actionBegin(args);
  }

  public assingAsset() {
    this.productAssetsMultiUploadComponent?.assignAssets(this.selectedProducts);
  }

  private onDrag(args: RowDragEventArgs): void {
    // if the drag is performed on the main row, then cancel the drag
    var row = args.target.closest('tr.e-row');
    if (row) {
      // if the row is a part of e-detailrow, then skip the row
      var detailRow = row.closest('tr.e-detailrow');
      if (detailRow) {
        return;
      }
      args.cancel = true;
    }
  }

  public onRowDrop(args: RowDropEventArgs): void {
    if (args.data[0] === undefined || args.dropIndex === undefined) {
      return;
    }
    var sourceItem, targetItem, dropIndex = args.dropIndex;

    var row = args.target.closest('tr.e-detailrow');
    if (row) {
      // get the row data

      const dataRow = row.previousElementSibling as HTMLElement;
      const uid = dataRow.getAttribute('data-uid');
      const rowObj = this.grid.getRowObjectFromUID(uid); // returns Row<...>
      var targetRowIndex = rowObj?.index; // zero-based index
      targetItem = this.grid.currentViewData[targetRowIndex] as ProductAssetDTOStargate;
    }

    var sourceItemRow = args.rows[0].closest('tr.e-detailrow');
    if (sourceItemRow) {

      const sourceDataRow = sourceItemRow.previousElementSibling as HTMLElement;
      const sourceUid = sourceDataRow.getAttribute('data-uid');
      const sourceRowObj = this.grid.getRowObjectFromUID(sourceUid); // returns Row<...>
      var sourceItemDataRowIndex = sourceRowObj?.index; // zero-based index
      sourceItem = this.grid.currentViewData[sourceItemDataRowIndex] as ProductAssetDTOStargate;

    }
    if (isNaN(args.dropIndex)) {
      dropIndex = 0;
    }
    //console.log('sourceItem: ' + sourceItem?.sKU + ' targetItem: ' + targetItem?.sKU + ' dropIndex: ' + dropIndex);
    if (!sourceItem || !targetItem) {
      args.cancel = true;
      return;
    }

    if (sourceItem.sKU === targetItem.sKU) {
      this.grid.showSpinner();
      this.productAssetsService.setNewOrderIndex((args.data[0] as IAssetDTOStargate).id, dropIndex).subscribe({
        next: () => {
          this.grid.hideSpinner();
          this.openExpandedRowOnRefresh = true;
          this.rowsToExpandAfterRefresh = [sourceItemDataRowIndex, targetRowIndex];
          this.grid.refresh();
        }, error: () => {
          this.grid.hideSpinner();
        }
      });
    }
    else {
      // copy asset to target product
      this.grid.showSpinner();
      this.productAssetsService.copyAsset((args.data[0] as IAssetDTOStargate).id, targetItem.sKU, dropIndex).subscribe({
        next: () => {
          this.grid.hideSpinner();
          this.openExpandedRowOnRefresh = true;
          this.rowsToExpandAfterRefresh = [sourceItemDataRowIndex, targetRowIndex];
          this.grid.refresh();
        }, error: () => {
          this.grid.hideSpinner();
        }
      });
    }
  }

  public dataBound(): void {
    //expand row after key attribute is set or expand first as default
    this.grid.detailRowModule.expand((this.openExpandedRowOnRefresh && this.expandedRowId) ? this.grid.getRowIndexByPrimaryKey(this.expandedRowId) : 0);
    this.openExpandedRowOnRefresh = false;

    //Reset selection after filter and sorting changed
    this.grid.dataStateChange.subscribe(() => {
      this.grid.clearSelection();
    });

    if (this.rowsToExpandAfterRefresh) {
      this.rowsToExpandAfterRefresh.forEach(rowIndex => {
        this.grid.detailRowModule.expand(rowIndex);
      });
      this.rowsToExpandAfterRefresh = null;
    }
  }

  public detailDataBound(e: DetailDataBoundEventArgs) {
    this.expandedRowId = (e.data as ProductAssetDTOStargate).sKU;
    e.childGrid.query = new Query();
    e.childGrid.dataSource = this.extendDataOfAssetList((e.data as ProductAssetDTOStargate).assets);
  }

  rowSelectedChanged() {
    this.selectedProducts = this.grid.getSelectedRecords() as ProductAssetDTOStargate[];
  }

  public getAssetTypeName(assetType: number): string {
    return 'i18n.shared.assetTypes.' + AssetType[assetType];
  }

  public getAssetSyncStatusName(assetSyncStatus: number): string {
    return 'i18n.shared.AssetSyncStatus.' + AssetSyncStatus[assetSyncStatus];
  }

  public setAssetAsKey(data: AssetDTOStargate) {
    this.productAssetsService.setAssetAsKey(data.id).subscribe(() => {
      this.openExpandedRowOnRefresh = true;
      this.grid.refresh();
    });
  }

  public editAsset(data: AssetDTOStargate) {
    this.productAssetsEditFormComponent?.editAsset(data);
  }

  public deleteAsset(data: AssetDTOStargate) {
    this.modalHelperService.confirm(
      'i18n.core.modal.delete.title',
      'i18n.core.modal.delete.content',
      () => {
        this.productAssetsService.deleteProductAsset(data.id).subscribe(() => {
          this.openExpandedRowOnRefresh = true;
          this.grid.refresh();
        });
      }
    );
  }

  public setAssetShopAsKey(assetId: number, shopId: string) {
    this.productAssetsService.setAssetShopAsKey(assetId, shopId).subscribe(() => {
      this.openExpandedRowOnRefresh = true;
      this.grid.refresh();
    });
  }

  public formatHasAssets(hasAssetsEnumeFilter: AssetStatusEnumFilterType): string {
    if ((hasAssetsEnumeFilter.hasAssets) as any == "true") {
      return this.translateService.instant('i18n.domain.assets.product-assets.filter.has-assets.with-assets');
    } else if ((hasAssetsEnumeFilter.hasAssets) as any === "false") {
      return this.translateService.instant('i18n.domain.assets.product-assets.filter.has-assets.without-assets');
    } else {
      return '';
    }
  }

  public formatSyncState(syncStateEnumFilter: ProductSyncStatusEnumFilterType): string {
    if (!syncStateEnumFilter || syncStateEnumFilter.syncState == null || syncStateEnumFilter.syncState == undefined) {
      return '';
    }
    return this.translateService.instant('i18n.shared.syncStatus.' + syncStateEnumFilter.syncState);
  }

  public getProductsAssetSyncState(productAsset: ProductAssetDTOStargate): string {
    if (productAsset.isPublishedToWeb === false) {
      return 'i18n.shared.syncStatus.Unpublished';
    }
    let syncCount = 0;
    let hasError = false;
    productAsset?.assets?.forEach(asset => {
      if (asset.syncStatus === AssetSyncStatus.Synced) {
        syncCount++;
      }
      if (asset.syncStatus === AssetSyncStatus.Error) {
        hasError = true;
      }
    });
    if (hasError) {
      return 'i18n.shared.syncStatus.Error';
    }
    if (syncCount == 0) {
      return 'i18n.shared.syncStatus.Pending';
    } else if (syncCount < productAsset.assets.length) {
      return 'i18n.shared.syncStatus.PartiallySynced';
    }

    return 'i18n.shared.syncStatus.Synced';
  }

  public hasError(productAsset: ProductAssetDTOStargate): boolean {
    return productAsset?.assets?.some(asset => asset.syncStatus === AssetSyncStatus.Error) ?? false;
  }

  public getPreviewLink(asset: AssetDTOStargate): string {
    if (asset?.fileExtension === 'pdf') {
      return 'assets/img/PDF_file_icon.svg';
    }

    if (this.assetConfigurationsService.assetConfiguration?.supportedAssetImageFileExtensions.includes('.' + asset?.fileExtension)) {
      return asset.fileLink;
    }

    return 'assets/img/document_unspecified.svg';
  }

  public OnCloseEditForm() {
    let selectedProducts = this.grid.getSelectedRecords() as ProductAssetDTOStargate[];
    this.grid.refresh()
    // keep track of the selected rows in order to expand them again after refresh (handled in dataBound()
    this.rowsToExpandAfterRefresh = selectedProducts.map(p => this.grid.getRowIndexByPrimaryKey(p.sKU));
  }

  protected initializeEditForm(data: any): void {
    throw new Error('Method not implemented.');
  }

  protected getEditDialogTitleParams(rowData: any): object {
    throw new Error('Method not implemented.');
  }

  private extendDataOfAssetList(assets: IAssetDTOStargate[]): IExtendedAssetDTOStargate[] {
    const extendedAssets = assets as IExtendedAssetDTOStargate[];
    extendedAssets.forEach(asset => {
      asset.canBeSetAsKey = true;
      asset.isDeletable = true;
      // Allow delete if there is only one asset of the same type
      var noOfAssetsOfSameType = extendedAssets.filter(a => a.type === asset.type).length;

      if (noOfAssetsOfSameType > 1 && asset.shops?.some(s => s.isKey)) {
        asset.isDeletable = false;
      }

      if (!asset.shops?.some(s => !s.isKey)) {
        asset.canBeSetAsKey = false;
      }
    });
    return extendedAssets.sort((a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER));
  }
}
